/**
 * TodosContext — ONE shared useTodos() instance for the whole signed-in shell.
 *
 * Before this, the Bell, the My-Team sidebar badge, and MyTeamPage each called
 * useTodos() and so each opened their own set of Firestore listeners. The
 * provider (mounted around <Layout> in App.tsx) runs the aggregator once;
 * every consumer reads the same live array via useSharedTodos(). This also
 * feeds the sidebar "Team" sub-nav count badges (Todo.teamRoute).
 *
 * useTodosLoading() (Sep 2026) is a SEPARATE opt-in hook for full pages that
 * would otherwise flash an empty state ("You're all caught up.") on first
 * load. useTodos() merges ~20 listeners/callables and has no per-source
 * loading flag, so this is a settle heuristic: loading ends as soon as any
 * to-do arrives, or SETTLE_MS after the profile is ready (the Firestore
 * listeners answer well within that). Bell/badges keep using useSharedTodos()
 * and never see it.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useTodos, type Todo } from './useTodos';
import { useProfile } from '../profile/ProfileProvider';

const SETTLE_MS = 1500;

const Ctx = createContext<Todo[] | null>(null);
const LoadingCtx = createContext<boolean>(false);

export function TodosProvider({ children }: { children: ReactNode }) {
  const todos = useTodos();
  const { state } = useProfile();
  const ready = state.status === 'ready';
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (settled) return;
    if (todos.length > 0) {
      setSettled(true);
      return;
    }
    if (!ready) return;
    const t = window.setTimeout(() => setSettled(true), SETTLE_MS);
    return () => window.clearTimeout(t);
  }, [settled, ready, todos.length]);
  return (
    <LoadingCtx.Provider value={!settled}>
      <Ctx.Provider value={todos}>{children}</Ctx.Provider>
    </LoadingCtx.Provider>
  );
}

export function useSharedTodos(): Todo[] {
  const v = useContext(Ctx);
  if (v === null) throw new Error('useSharedTodos must be used inside <TodosProvider>');
  return v;
}

/** True until the shared to-do list has had a chance to fill on first load. */
export function useTodosLoading(): boolean {
  return useContext(LoadingCtx);
}
